import { Box, useToken } from '@chakra-ui/react';
import dayjs from 'dayjs';
import { useMemo, useRef, useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { IoEyeOutline } from 'react-icons/io5';
import { MdEdit, MdSupportAgent } from 'react-icons/md';
import { useSearchParams } from 'react-router';
import FaceliftDataLoader from '@/components/Atoms/FaceliftDataLoader';
import FaceliftDialog from '@/components/Atoms/FaceliftDialog';
import FaceliftTruncatedTextWithTooltip from '@/components/Atoms/TruncatedTextWithTooltip';
import FaceliftDialogWithExitConfirmation from '@/components/Molecules/FaceliftDialogWithExitConfirmation';
import type { ExitConfirmationDraftAction } from '@/components/Molecules/FaceliftDialogWithExitConfirmation';
import FaceliftSuccessProgressModal from '@/components/Molecules/FaceliftSuccessProgressModal';
import Show from '@/components/Molecules/Show';
import DataTable from '@/components/Organisms/DataTable';
import type {
	ColumnData,
	onFilterApplyProps,
} from '@/components/Organisms/DataTable/DataTable.types';
import DetailsView from '@/components/Templates/DetailsView';
import ClaimsView from '@/components/Templates/ManagePatients/ClaimsModal';
import PatientForm, {
	type PatientFormData,
} from '@/components/Templates/ManagePatients/PatientForm';
import { SupportTicketForm } from '@/components/Templates/SupportTickets/SupportTicketForm';
import {
	BgProcessModule,
	useCreateBgProcess,
	useUpdateBgProcess,
} from '@/hooks/service-hooks/bgProcess';
import { useGetPatient, useGetPatients } from '@/hooks/service-hooks/patients';
import { useManagePatientsTutorial } from '@/hooks/tutorialsHooks/useGraftLogsTutorial';
import { useClinicFilter } from '@/hooks/useClinicFilter';
import usePermission from '@/hooks/usePermission';
import { getPatients } from '@/services/patients';
import { useAuthStore } from '@/store';
import { useTourContext } from '@/store/tourContext';
import type { Patients } from '@/types/patient';
import type { FilterType } from '@/types/table';
import { trackCoreClick } from '@/utils/analytics';
import { parsePatientDataForDetailsModal } from '@/utils/detailsModalUtils';
import {
	generateCSVFromTable,
	generateExcelFromTable,
} from '@/utils/exportFiles';
import { generateMockPatientsData } from '@/utils/tutorials/mockData';
import { columns } from './patient';

const ManagePatientsTable = () => {
	const managePatientsTableRef = useRef<HTMLDivElement | null>(null);
	const { hasPermission } = usePermission();
	const [brand600] = useToken('colors', ['brand.600']);
	const [searchParams, setSearchParams] = useSearchParams();
	
	// Shared clinic filter helpers (key used by patients API)
	const { defaultClinicIds, mapGlobalFilters } = useClinicFilter(
		'_parentcustomerid_value',
	);

	const user = useAuthStore((state) => state.user);
	const userId = user?.userInformation?._id || null;

	const [filters, setFilters] = useState<FilterType>({
		page: 1,
		limit: 15,
		columnsFilters: '[]',
		search: '',
		enabled: defaultClinicIds.length > 0,
		status: '',
		globalFilters: JSON.stringify(
			defaultClinicIds.length
				? [
						{
							key: '_parentcustomerid_value',
							values: defaultClinicIds,
						},
					]
				: [],
		),
		sort: '',
	});

	// Modal state management
	const [modalInformation, setModalInformation] = useState<{
		mode: 'create' | 'edit' | 'details' | 'claims' | 'supportTicket' | null;
		data: string | Partial<PatientFormData> | null;
		patientFullName?: string; // For support ticket subject
		clinicData?: { value: string; label: string } | null; // For support ticket clinic pre-population
	}>({
		mode: null,
		data: null,
	});

	const [isFormDirty, setIsFormDirty] = useState(false);
	const [isSupportTicketFormDirty, setIsSupportTicketFormDirty] = useState(false);
	const [patientExitDraftAction, setPatientExitDraftAction] =
		useState<ExitConfirmationDraftAction | null>(null);
	const [supportTicketExitDraftAction, setSupportTicketExitDraftAction] =
		useState<ExitConfirmationDraftAction | null>(null);

	const { data, isFetching, refetch } = useGetPatients(filters);
	const { activeTutorialName } = useTourContext();
	const isGraftLogsTutorialActive = activeTutorialName === 'OnboardingTutorialGraftLogs';

	// Register tutorial refs
	useManagePatientsTutorial({
		managePatientsTableRef,
	});

	// Get individual patient data for details modal
	const { data: patientDetails, isFetching: isPatientDetailsLoading } =
		useGetPatient(
			modalInformation.mode === 'details'
				? (modalInformation.data as string)
				: null,
			modalInformation.mode === 'details',
		);

	// Transform API data to table format
	const transformedData = useMemo(() => {
		// IMPORTANT: Only use mock data when the GraftLogs tutorial is ACTIVE
		// When tutorial is NOT active, use real API data
		if (isGraftLogsTutorialActive) {
			// Tutorial is active - use mock data
			const mockData = generateMockPatientsData(10);
			return mockData.map((item) => {
				return {
					contactid: item.contactid,
					id: item.contactid,
					fullname: (
						<FaceliftTruncatedTextWithTooltip
							text={item.fullname || '-'}
							maxLength={20}
						/>
					),
					'account.name': (
						<FaceliftTruncatedTextWithTooltip
							text={item.account?.name || '-'}
							maxLength={20}
						/>
					),
					address1_line1: (
						<FaceliftTruncatedTextWithTooltip
							text={item.address1_line1 || '-'}
							maxLength={20}
						/>
					),
					ta_emrid: (
						<FaceliftTruncatedTextWithTooltip
							text={item.ta_emrid || '-'}
							maxLength={20}
						/>
					),
					'primaryInsurance.ta_name': (
						<FaceliftTruncatedTextWithTooltip
							text={item.primaryInsurance?.ta_name || '-'}
							maxLength={50}
						/>
					),
					ta_primarymemberid: (
						<FaceliftTruncatedTextWithTooltip
							text={item.ta_primarymemberid || '-'}
							maxLength={20}
						/>
					),
				};
			});
		}

		if (!data?.items) return [];

		return data.items.map((item) => {
			return {
				contactid: item.contactid,
				id: item.contactid,
				fullname: (
					<FaceliftTruncatedTextWithTooltip
						text={item.fullname || '-'}
						maxLength={20}
					/>
				),
				'account.name': (
					<FaceliftTruncatedTextWithTooltip
						text={item.account?.name || '-'}
						maxLength={20}
					/>
				),
				address1_line1: (
					<FaceliftTruncatedTextWithTooltip
						text={item.address1_line1 || '-'}
						maxLength={20}
					/>
				),
				ta_emrid: (
					<FaceliftTruncatedTextWithTooltip
						text={item.ta_emrid || '-'}
						maxLength={20}
					/>
				),
				'primaryInsurance.ta_name': (
					<FaceliftTruncatedTextWithTooltip
						text={item.primaryInsurance?.ta_name || '-'}
						maxLength={50}
					/>
				),
				ta_primarymemberid: (
					<FaceliftTruncatedTextWithTooltip
						text={item.ta_primarymemberid || '-'}
						maxLength={20}
					/>
				),
			};
		});
	}, [data, isGraftLogsTutorialActive]);
	const transformedDataToExport = (data: Patients[]) => {
		if (!data) return [];

		return data.map((item) => {
			return {
				contactid: item.contactid,
				id: item.contactid,
				fullname: item.fullname || '-',
				'account.name': item.account?.name || '-',
				address1_line1: item.address1_line1 || '-',
				ta_emrid: item.ta_emrid || '-',
				'primaryInsurance.ta_name': item.primaryInsurance?.ta_name || '-',
				ta_primarymemberid: item.ta_primarymemberid || '-',
			};
		});
	};

	// Handle filter apply
	const onFilterApply = ({
		page,
		limit,
		status,
		search = '',
		sort = null,
		columnsFilters = [],
		globalFilters = [],
	}: onFilterApplyProps) => {
		const mappedFilters = mapGlobalFilters(globalFilters || []);

		setFilters({
			page,
			limit,
			columnsFilters: JSON.stringify(columnsFilters),
			search,
			enabled: defaultClinicIds.length > 0,
			status: status?.value,
			globalFilters: JSON.stringify(mappedFilters),
			sort: sort ? `${sort.key}|${sort.direction}` : '',
		});
	};

	// Handle selection
	const onSelection = (ids: string[]) => {
		// Handle selection logic here if needed
	};

	// Handle create patient
	const handleCreatePatient = () => {
		trackCoreClick('create_patient_clicked', {
			source_module: 'manage_patients_table',
		});
		setModalInformation({
			mode: 'create',
			data: null,
		});
	};

	// Handle view patient details
	const handleViewDetails = (patientId: string) => {
		trackCoreClick('view_patient_details_clicked', {
			source_module: 'manage_patients_table',
			patient_id: patientId,
		});
		setModalInformation({
			mode: 'details',
			data: patientId,
		});
	};

	// Handle view claims/orders
	const handleViewClaims = (patientId: string) => {
		trackCoreClick('view_patient_order_history_clicked', {
			source_module: 'manage_patients_table',
			patient_id: patientId,
		});
		setModalInformation({
			mode: 'claims',
			data: patientId,
		});
	};

	// Handle edit patient
	const handleEditPatient = (patientId: string) => {
		// Find the patient from data array using the ID
		const patient = data?.items.find((item) => item.contactid === patientId);

		if (patient) {
			trackCoreClick('edit_patient_clicked', {
				source_module: 'manage_patients_table',
				patient_id: patientId,
				clinic_id: patient.account?.accountid || '',
			});
			const patientData = {
				contactid: patient.contactid,
				id: patient.contactid,
				// Personal Information
				firstname: patient.firstname || patient.fullname?.split(' ')[0] || '',
				lastname:
					patient.lastname ||
					patient.fullname?.split(' ').slice(1).join(' ') ||
					'',
				birthdate: patient.birthdate || '',
				gendercode: patient.gendercode ? String(patient.gendercode) : '',

				// Contact Information
				emailaddress1: patient.emailaddress1 || '',
				mobilephone: patient.mobilephone || '',
				preferredcontactmethodcode: patient.preferredcontactmethodcode
					? String(patient.preferredcontactmethodcode)
					: '',

				// Medical Information
				ta_emrid: patient.ta_emrid || '',
				ta_primarymemberid: patient.ta_primarymemberid || '',
				ta_secondarymemberid: patient.ta_secondarymemberid || '',

				// Insurance Information (extracting IDs from nested objects)
				primaryInsurance: patient.primaryInsurance?.ta_insuranceid
					? {
							value: patient.primaryInsurance?.ta_insuranceid || '',
							label: patient.primaryInsurance?.ta_name || '',
						}
					: null,
				secondaryInsurance: patient.secondaryInsurance?.ta_insuranceid
					? {
							value: patient.secondaryInsurance?.ta_insuranceid || '',
							label: patient.secondaryInsurance?.ta_name || '',
						}
					: null,

				// Address Information
				address1_line1: patient.address1_line1 || '',
				address1_city: patient.address1_city || '',
				address1_stateorprovince: patient.address1_stateorprovince || '',
				address1_postalcode: patient.address1_postalcode || '',

				// Additional Information
				customertypecode: patient.customertypecode || '',
				clinic: patient.account?.accountid
					? {
							value: patient.account?.accountid || '',
							label: patient.account?.name || '',
						}
					: null,
				ta_originationsource: patient.ta_originationsource || '',
			};

			setModalInformation({
				mode: 'edit',
				data: patientData,
			});
		}
	};

	const handleCloseModal = () => {
		setModalInformation({ mode: null, data: null });
		setIsFormDirty(false);
	};
	const [successMessage, setSuccessMessage] = useState('');

	// Mutations for create and update
	const {
		mutate: createPatient,
		isLoading: createLoading,
		isSuccess: createSuccess,
		isError: createError,
	} = useCreateBgProcess(() => {
		setSuccessMessage('Patient created successfully');
		handleCloseModal();
		refetch();
	});

	const {
		mutate: updatePatient,
		isLoading: updateLoading,
		isError: updateError,
		isSuccess: updateSuccess,
	} = useUpdateBgProcess(() => {
		setSuccessMessage('Patient updated successfully');
		handleCloseModal();
		refetch();
	});

	// Handle form submission
	const handleSubmitPatient = async (formData: PatientFormData) => {
		try {
			// Transform form data to API payload format
			const payload = {
				firstname: formData.firstname,
				ta_emrid: formData.ta_emrid,
				lastname: formData.lastname,
				gendercode: formData.gendercode,
				ta_primarymemberid: formData.ta_primarymemberid,
				preferredcontactmethodcode: formData.preferredcontactmethodcode,
				mobilephone: formData.mobilephone,
				ta_secondarymemberid: formData.ta_secondarymemberid,
				emailaddress1: formData.emailaddress1,
				address1_line1: formData.address1_line1,
				address1_city: formData.address1_city,
				address1_stateorprovince: formData.address1_stateorprovince,
				address1_postalcode: formData.address1_postalcode,
				birthdate: dayjs(formData.birthdate, 'MM/DD/YYYY').format('YYYY-MM-DD'),
				'ta_PrimaryInsurance@odata.bind': formData.primaryInsurance?.value
					? `ta_insurances(${formData.primaryInsurance?.value})`
					: undefined,
				'ta_SecondaryInsurance@odata.bind': formData.secondaryInsurance?.value
					? `ta_insurances(${formData.secondaryInsurance?.value})`
					: undefined,
				'parentcustomerid_account@odata.bind': formData.clinic?.value
					? `accounts(${formData.clinic?.value})`
					: undefined,
				ta_originationsource: '953870002',
				customertypecode: '100000011',
			};
			trackCoreClick('submit_patient_clicked', {
				source_module: 'manage_patients_form',
				form_mode: modalInformation.mode || '',
				patient_id:
					modalInformation.mode === 'edit'
						? ((modalInformation.data as Partial<PatientFormData>)?.id || '')
						: '',
				clinic_id: formData.clinic?.value || '',
				has_primary_insurance: Boolean(formData.primaryInsurance?.value),
				has_secondary_insurance: Boolean(formData.secondaryInsurance?.value),
			});

			if (modalInformation.mode === 'create') {
				await createPatient({
					moduleName: BgProcessModule.PATIENTS,
					userId: userId || '',
					payload: {
						payload,
						additionalPayload: {
							clinicName: formData.clinic?.label as string,
							fullName: `${formData.firstname} ${formData.lastname}`,
							primaryInsuranceName: formData.primaryInsurance?.label as string,
							secondaryInsuranceName: formData.secondaryInsurance?.label as string,
						} 
					},
				});
			} else if (modalInformation.mode === 'edit') {
				// For update, we need the patient ID
				const patientId = (modalInformation.data as Partial<PatientFormData>)
					?.id;
				if (patientId && modalInformation.mode === 'edit') {
					await updatePatient({
						moduleName: BgProcessModule.PATIENTS,
						userId: userId || '',
						payload: {
							payload,
							additionalPayload: {
								clinicName: formData.clinic?.label as string,
								fullName: `${formData.firstname} ${formData.lastname}`,
								primaryInsuranceName: formData.primaryInsurance?.label as string,
								secondaryInsuranceName: formData.secondaryInsurance?.label as string,
							} 
						},
						crmId: patientId,
					});
				}
			}
		} catch (error) {
			console.error('Error submitting patient:', error);
			// Error handling is now done in the mutation hooks
		}
	};
	const exportAllAsCSV = async () => {
		try {
			// Fetch all data with current filters
			const data = await getPatients(
				1,
				9999,
				filters.columnsFilters,
				filters.search,
				String(filters.status || ''),
				filters.globalFilters,
				String(filters.sort || ''),
			);

			const transformedData = transformedDataToExport(data?.items || []);
			generateCSVFromTable(columns, transformedData, `Patients-all`, true);
		} catch (error) {
			console.error('Error exporting CSV:', error);
			toast.error('Failed to export CSV');
		}
	};

	const exportAllAsExcel = async () => {
		try {
			// Fetch all data with current filters
			const data = await getPatients(
				1,
				9999,
				filters.columnsFilters,
				filters.search,
				String(filters.status || ''),
				filters.globalFilters,
				String(filters.sort || ''),
			);

			const transformedData = transformedDataToExport(data?.items || []);
			generateExcelFromTable(columns, transformedData, `Patients-all`, true);
		} catch (error) {
			console.error('Error exporting Excel:', error);
			toast.error('Failed to export Excel');
		}
	};

	return (
		<>
			<Box
				p={{ base: 4, md: 8 }}
				py={{ base: 4, md: 6 }}
				bg={{ base: '{colors.gray.50}', _dark: '{colors.gray.900}' }}
			>
				<DataTable
					ref={managePatientsTableRef}
					tableName="Patients"
					loading={isFetching}
					onSelection={onSelection}
					items={transformedData}
					columns={columns}
					topLabel="Patients"
					totalCount={data?.meta.totalRecords || 0}
					onFilterApply={onFilterApply}
					expandable
					fullscreen
					paginated
					exportable
					hideShowColumn
					excel
					exportAllAsCSV={exportAllAsCSV}
					exportAllAsExcel={exportAllAsExcel}
					searchable
					csv
					bordered
					globalFilter={{
						title: 'Filters',
						clinics: true,
					}}
					{...hasPermission(['Patients:Create']) && {
            creatableFn: handleCreatePatient
          }}
					actions={[
						{
							label: 'Edit',
							icon: <MdEdit />,
							show: () => hasPermission(['Patients:Edit']),
							actionFn: (item: ColumnData) => {
								const patientId = item.contactid as string;
								if (patientId) {
									handleEditPatient(patientId);
								}
							},
						},
						{
							label: 'Details',
							icon: <IoEyeOutline />,
							show: () => hasPermission(['Patients:Visualize']),
							actionFn: (item: ColumnData) => {
								const patientId = item.contactid as string;
								if (patientId) {
									handleViewDetails(patientId);
								}
							},
						},
						{
							label: 'View Order History',
							icon: <IoEyeOutline />,
							show: () => hasPermission(['Orders:Visualize']),
							actionFn: (item: ColumnData) => {
								const patientId = item.contactid as string;
								if (patientId) {
									handleViewClaims(patientId);
								}
							},
						},
						{
							label: 'Support Ticket',
							icon: <MdSupportAgent />,
							show: () => hasPermission(['SupportTickets:Create']),
							actionFn: (item: ColumnData) => {
								const patientId = item.contactid as string;
								if (patientId) {
									// Find the patient from data array using the ID
									const patient = data?.items.find((item) => item.contactid === patientId);
									
									if (patient) {
										const patientFullName = patient.fullname || '';
										const clinicData = patient.account?.accountid && patient.account?.name
											? { value: patient.account.accountid, label: patient.account.name }
											: null;

										setIsSupportTicketFormDirty(false);
										setModalInformation({
											mode: 'supportTicket',
											data: patientId,
											patientFullName: patientFullName,
											clinicData: clinicData,
										});
									}
								}
							},
						},
					]}
				/>
			</Box>

			<FaceliftDialogWithExitConfirmation
				showBackBtn={true}
				open={
					modalInformation.mode === 'create' || modalInformation.mode === 'edit'
				}
				onOpenChange={(open) => {
					if (!open) handleCloseModal();
				}}
				title={
					modalInformation.mode === 'create' ? 'Create Patient' : 'Edit Patient'
				}
				size="xl"
				sx={{
					width: 900,
					maxWidth: '95vw',
					maxHeight: '85vh',
					boxShadow: `0 0 20px 0px ${brand600}`,
					overflowX: 'visible',
					overflowY: 'auto',
				}}
				isDirty={isFormDirty}
				saveDraftAction={patientExitDraftAction}
			>
				<PatientForm
					mode={modalInformation.mode as 'create' | 'edit'}
					initialData={modalInformation.data}
					onSubmit={handleSubmitPatient}
					isLoading={createLoading || updateLoading}
					onIsDirtyChange={setIsFormDirty}
					onExitConfirmationDraftActionChange={
						modalInformation.mode === 'create' || modalInformation.mode === 'edit'
							? setPatientExitDraftAction
							: undefined
					}
					onSuccess={handleCloseModal}
				/>
			</FaceliftDialogWithExitConfirmation>
			<FaceliftSuccessProgressModal
				open={
					createLoading ||
					updateLoading ||
					createSuccess ||
					updateSuccess ||
					createError ||
					updateError
				}
				isLoading={createLoading || updateLoading}
				isSucceed={createSuccess || updateSuccess}
				hasError={createError || updateError}
				onOpenChange={() => {}}
				title={successMessage}
				errorMessage={
					createError
						? 'Error creating patient'
						: updateError
							? 'Error updating patient'
							: ''
				}
			/>

			<FaceliftDialog
				showBackBtn={true}
				title="Details"
				open={modalInformation.mode === 'details'}
				onOpenChange={(open) => {
					if (!open) handleCloseModal();
				}}
				sx={{
					width: '90vw',
					maxWidth: '95vw',
					boxShadow: `0 0 20px 0px ${brand600}`,
				}}
			>
				<Show when={isPatientDetailsLoading}>
					<FaceliftDataLoader
						title="Loading patient details..."
						description="Please wait while we fetch the patient information."
					/>
				</Show>
				<Show when={!isPatientDetailsLoading}>
					<DetailsView
						data={
							patientDetails
								? parsePatientDataForDetailsModal(patientDetails)
								: []
						}
						isLoading={false}
					/>
				</Show>
			</FaceliftDialog>

			<FaceliftDialog
				showBackBtn={true}
				title="Patient's Order History"
				open={modalInformation.mode === 'claims'}
				onOpenChange={(open) => {
					if (!open) handleCloseModal();
				}}
				size="xl"
				sx={{ boxShadow: `0 0 20px 0px ${brand600}` }}
			>
				<ClaimsView id={modalInformation.data as string} />
			</FaceliftDialog>

			{/* Support Ticket Modal */}
			<FaceliftDialogWithExitConfirmation
				open={modalInformation.mode === 'supportTicket'}
				onOpenChange={(openState) => {
					if (!openState) {
						setModalInformation({ mode: null, data: null });
						setIsSupportTicketFormDirty(false);
					}
				}}
				title="Create Support Ticket"
				showBackBtn={true}
				size="xl"
				sx={{
					boxShadow: `0 0 20px 0px ${brand600}`,
					width: '840px',
					maxWidth: '95vw',
					overflowY: 'auto',
				}}
				isDirty={isSupportTicketFormDirty}
				saveDraftAction={supportTicketExitDraftAction}
			>
				<SupportTicketForm
					mode="create"
					initialData={{
						clinic: modalInformation.clinicData || null,
						type: { value: '953870001', label: 'Patients' },
						subject: modalInformation.patientFullName || '',
						status: { value: '1', label: 'In Progress' },
					}}
					onSuccess={() => {
						setModalInformation({ mode: null, data: null });
						setIsSupportTicketFormDirty(false);
						refetch();
					}}
					onIsDirtyChange={setIsSupportTicketFormDirty}
					onExitConfirmationDraftActionChange={
						modalInformation.mode === 'supportTicket'
							? setSupportTicketExitDraftAction
							: undefined
					}
				/>
			</FaceliftDialogWithExitConfirmation>
		</>
	);
};

export default ManagePatientsTable;
